import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const StudentConfirmOTP = () => {
  const [otpValues, setOtpValues] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [verifyMsg, setVerifyMsg] = useState("");

  useEffect(() => {
    const stateEmail = location.state?.email;
    const storedEmail = localStorage.getItem("studentResellerSignupEmail");
    const nextEmail = stateEmail || storedEmail || "";
    if (nextEmail) {
      setEmail(nextEmail);
    }
    if (location.state?.successMessage) {
      setVerifyMsg(location.state.successMessage);
    }
  }, [location.state]);

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextValues = [...otpValues];
    nextValues[index] = digit;
    setOtpValues(nextValues);

    if (digit && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const isComplete = otpValues.every((value) => value.length === 1);

  const handleConfirm = () => {
    if (!email) {
      setVerifyMsg("Email is required.");
      return;
    }
    setIsLoading(true);
    setVerifyMsg("");
    const otp = otpValues.join("");
    fetch(`${process.env.REACT_APP_API_URL}/api/student-resellers/verify-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp }),
    })
      .then(async (res) => {
        const result = await res.json();
        return { ok: res.ok, result };
      })
      .then(({ ok, result }) => {
        setIsLoading(false);
        if (ok && result.success) {
          if (result.token) {
            localStorage.removeItem("studentResellerToken");
            localStorage.setItem("studentResellerToken", result.token);
          }
          if (result?.data?.fullName) {
            localStorage.setItem("studentResellerFullName", result.data.fullName);
          }
          if (result?.data?.email) {
            localStorage.setItem("studentResellerEmail", result.data.email);
          }
          setVerifyMsg(result.message || "Student reseller account verified successfully.");
          setTimeout(() => navigate("/student-reseller"), 1000);
          return;
        }

        setVerifyMsg(result.message || "Verification failed. Please try again.");
      })
      .catch(() => {
        setIsLoading(false);
        setVerifyMsg("Network error. Please try again.");
      });
  };

  const handleResend = () => {
    if (!email) {
      setVerifyMsg("Email is required.");
      return;
    }
    setIsLoading(true);
    setVerifyMsg("");
    fetch(`${process.env.REACT_APP_API_URL}/api/student-resellers/resend-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    })
      .then(async (res) => {
        const result = await res.json();
        return { ok: res.ok, result };
      })
      .then(({ result }) => {
        setIsLoading(false);
        setVerifyMsg(result.message || "Unable to resend code right now.");
      })
      .catch(() => {
        setIsLoading(false);
        setVerifyMsg("Network error. Please try again.");
      });
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-white">
      <div className="w-full max-w-md px-4 pt-6 pb-24">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#19C463] rounded"
            aria-label="Back"
          >
            &lt;
          </button>
          <h1 className="text-lg font-semibold">Confirm Code</h1>
        </div>

        <p className="mt-4 text-gray-600">
          A confirmation code has been sent to <span>{email || "user@gmail.com"}</span>.
        </p>
        {verifyMsg && (
          <div className="mb-4 text-center bg-[#FFBABA] text-[#800000] px-4 py-2 rounded-lg font-semibold animate-fadeInOut">
            {verifyMsg}
          </div>
        )}

        <div className="mt-6 flex justify-center gap-3">
          {otpValues.map((value, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={value}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              className="h-12 w-12 text-center text-lg rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:border-[#19C463]"
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          <span>Didn't receive code? </span>
          <button type="button" onClick={handleResend} className="text-[#19C463] font-medium">
            Resend
          </button>
        </div>
        <div className="mt-2 text-center text-sm text-gray-600">or</div>
        <div className="mt-2 text-center text-sm text-gray-600">
          Change <button type="button" className="text-[#19C463] font-medium">email</button> to receive code
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 bg-white p-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <button
          type="button"
          disabled={!isComplete}
          onClick={handleConfirm}
          className={`w-full max-w-md mx-auto flex justify-center py-3 px-4 rounded-md bg-[#19C463] text-white text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-[#19C463] focus:ring-offset-2 ${
            isComplete ? "" : "opacity-50 cursor-not-allowed"
          }`}
        >
          Confirm
        </button>
      </div>

      {isLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="rounded-[12px] bg-white p-6 flex flex-col items-center">
            <div className="h-10 w-10 border-4 border-[#19C463] border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentConfirmOTP;
